import React, { useEffect, useState } from 'react'
import { Layout, Menu, Dropdown, Avatar, Button, Drawer, theme } from 'antd'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  DesktopOutlined,
  BulbOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  MessageOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  AuditOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import useIsMobile from '../../hooks/useIsMobile'
import { resolvePublicUrl } from '../../utils/publicUrl'
import './MainLayout.css'
import './MainLayout.mobile.css'

const { Header, Sider, Content } = Layout

const PAGE_TITLES: Record<string, string> = {
  '/': '工作台',
  '/dashboard': '数据看板',
  '/analysis-review': '分析评审',
  '/insights': '洞察分析',
  '/reports': '报告中心',
  '/quality': '质检中心',
  '/chat': '智能助手',
  '/settings': '系统设置',
}

interface MainLayoutProps {
  children: React.ReactNode
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [avatarFailed, setAvatarFailed] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, hasPermission } = useAuthStore()
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken()
  const isMobile = useIsMobile()
  const pageTitle = PAGE_TITLES[location.pathname] || '客服会话洞察'
  const avatarUrl = resolvePublicUrl(user?.avatar)

  useEffect(() => {
    setAvatarFailed(false)
  }, [avatarUrl])

  const allMenuItems = [
    {
      key: '/',
      icon: <DesktopOutlined />,
      label: '工作台',
      permission: 'workspace:view',
    },
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '数据看板',
      permission: 'dashboard:view',
    },
    {
      key: '/analysis-review',
      icon: <AuditOutlined />,
      label: '分析评审',
      permission: 'analysis_review:view',
    },
    {
      key: '/insights',
      icon: <BulbOutlined />,
      label: '洞察分析',
      permission: 'insights:view',
    },
    {
      key: '/reports',
      icon: <FileTextOutlined />,
      label: '报告中心',
      permission: 'reports:view',
    },
    {
      key: '/quality',
      icon: <SafetyCertificateOutlined />,
      label: '质检中心',
      permission: 'quality:view',
    },
    {
      key: '/chat',
      icon: <MessageOutlined />,
      label: '智能助手',
      permission: 'chat:view',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      permission: 'settings:account',
    },
  ]

  const menuItems = allMenuItems.filter(item => hasPermission(item.permission))

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ]

  const handleMenuClick = (e: { key: string }) => {
    navigate(e.key)
    if (isMobile) setMobileMenuOpen(false)
  }

  const handleUserMenuClick = (e: { key: string }) => {
    if (e.key === 'logout') {
      logout()
      navigate('/login')
    } else if (e.key === 'profile') {
      navigate('/settings')
    }
  }

  return (
    <Layout className={`main-layout${isMobile ? ' main-layout-mobile' : ''}`}>
      {!isMobile && <Sider trigger={null} collapsible collapsed={collapsed} className="sider desktop-sider">
        <div className="logo">
          {logoFailed ? (
            <MessageOutlined className="logo-fallback-icon" />
          ) : (
            <img src="/logo.png" alt="" className="logo-img" onError={() => setLogoFailed(true)} />
          )}
          {collapsed ? '洞察' : '客服会话洞察'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>}
      <Layout>
        <Header className="header" style={{ background: colorBgContainer }}>
          <div className="header-left">
            <Button
              type="text"
              icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
              onClick={() => {
                if (isMobile) {
                  setMobileMenuOpen(true)
                  return
                }
                setCollapsed(!collapsed)
              }}
              className="trigger"
              aria-label={isMobile ? '打开页面导航' : '折叠页面导航'}
            />
            {isMobile && <span className="mobile-page-title">{pageTitle}</span>}
          </div>
          <div className="header-right">
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div className="user-info">
                <Avatar
                  src={!avatarFailed ? avatarUrl : undefined}
                  icon={(!avatarUrl || avatarFailed) && <UserOutlined />}
                  onError={() => {
                    setAvatarFailed(true)
                    return true
                  }}
                  size="small"
                />
                <span className="username">{user?.username || '用户'}</span>
                {user?.roleDisplayName && (
                  <span className="role-name" style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>
                    ({user.roleDisplayName})
                  </span>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          className="content"
          style={{
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          {children}
        </Content>
      </Layout>
      <Drawer
        title={null}
        placement="left"
        width={260}
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        className="mobile-nav-drawer"
        styles={{ body: { padding: 0 } }}
      >
        <div className="mobile-drawer-logo">
          {logoFailed ? (
            <MessageOutlined className="logo-fallback-icon" />
          ) : (
            <img src="/logo.png" alt="" className="logo-img" onError={() => setLogoFailed(true)} />
          )}
          <span>客服会话洞察</span>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Drawer>
    </Layout>
  )
}

export default MainLayout
